import React from 'react';
import { BoldOutlined, CodeOutlined, ItalicOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Row, Space, Spin, Tooltip } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';

interface CustomerEmailEditorModalProps {
  open: boolean;
  title: string;
  recipientName?: string | null;
  recipientEmail?: string | null;
  subject: string;
  message: string;
  previewHtml: string;
  editing: boolean;
  previewAvailable: boolean;
  previewLoading: boolean;
  sending: boolean;
  okText: string;
  previewErrorText: string;
  onCancel: () => void;
  onSubmit: () => void;
  onEdit: () => void;
  onDiscardEdit: () => void;
  onSubjectChange: (subject: string) => void;
  onMessageChange: (message: string) => void;
}

const CustomerEmailEditorModal: React.FC<CustomerEmailEditorModalProps> = ({
  open,
  title,
  recipientName,
  recipientEmail,
  subject,
  message,
  previewHtml,
  editing,
  previewAvailable,
  previewLoading,
  sending,
  okText,
  previewErrorText,
  onCancel,
  onSubmit,
  onEdit,
  onDiscardEdit,
  onSubjectChange,
  onMessageChange,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const messageTextAreaRef = React.useRef<TextAreaRef>(null);
  const canSubmit = Boolean(previewAvailable && subject.trim() && (!editing || message.trim()));
  const formattingDisabled = sending;

  const applyInlineFormat = (prefix: string, suffix = prefix) => {
    const textArea = messageTextAreaRef.current?.resizableTextArea?.textArea;
    const start = textArea?.selectionStart ?? message.length;
    const end = textArea?.selectionEnd ?? message.length;
    const selectedText = message.slice(start, end);
    const replacement = `${prefix}${selectedText}${suffix}`;
    const nextMessage = `${message.slice(0, start)}${replacement}${message.slice(end)}`;
    const nextSelectionStart = start + prefix.length;
    const nextSelectionEnd = nextSelectionStart + selectedText.length;

    onMessageChange(nextMessage);
    window.requestAnimationFrame(() => {
      const nextTextArea = messageTextAreaRef.current?.resizableTextArea?.textArea;
      nextTextArea?.focus();
      nextTextArea?.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };

  const applyCodeBlockFormat = () => {
    const textArea = messageTextAreaRef.current?.resizableTextArea?.textArea;
    const start = textArea?.selectionStart ?? message.length;
    const end = textArea?.selectionEnd ?? message.length;
    const selectedText = message.slice(start, end);
    const before = message.slice(0, start);
    const after = message.slice(end);
    const leadingBreak = before && !before.endsWith('\n') ? '\n' : '';
    const trailingBreak = after && !after.startsWith('\n') ? '\n' : '';
    const prefix = `${leadingBreak}\`\`\`\n`;
    const suffix = `\n\`\`\`${trailingBreak}`;
    const replacement = `${prefix}${selectedText}${suffix}`;
    const nextMessage = `${before}${replacement}${after}`;
    const nextSelectionStart = start + prefix.length;
    const nextSelectionEnd = nextSelectionStart + selectedText.length;

    onMessageChange(nextMessage);
    window.requestAnimationFrame(() => {
      const nextTextArea = messageTextAreaRef.current?.resizableTextArea?.textArea;
      nextTextArea?.focus();
      nextTextArea?.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText={okText}
      cancelText={t('common:cancel')}
      okButtonProps={{
        loading: sending,
        disabled: previewLoading || !canSubmit,
      }}
      cancelButtonProps={{ disabled: sending }}
      width={1040}
    >
      <div style={{ marginBottom: 12, fontSize: 13, color: '#595959' }}>
        <div><strong>{t('orders:orderDetail.customer')}:</strong> {recipientName || '-'}</div>
        <div><strong>{t('common:email')}:</strong> {recipientEmail || '-'}</div>
      </div>
      {previewLoading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
      ) : previewAvailable ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ marginBottom: 4, fontWeight: 600 }}>{t('orders:orderDetail.subject')}</div>
              <div style={{ color: '#262626', wordBreak: 'break-word' }}>{subject || '-'}</div>
            </div>
            {editing ? (
              <Button onClick={onDiscardEdit} disabled={sending}>
                {t('orders:orderDetail.discardEmailEdit')}
              </Button>
            ) : (
              <Button onClick={onEdit} disabled={sending}>
                {t('orders:orderDetail.editEmailMessage')}
              </Button>
            )}
          </div>

          {editing ? (
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>{t('orders:orderDetail.subject')}</div>
                    <Input
                      value={subject}
                      onChange={(event) => onSubjectChange(event.target.value)}
                      maxLength={200}
                      showCount
                      disabled={sending}
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>{t('orders:orderDetail.emailMessage')}</div>
                    <Space size={4} style={{ marginBottom: 8 }}>
                      <Tooltip title={t('orders:orderDetail.emailFormattingBold')}>
                        <Button
                          type="text"
                          size="small"
                          icon={<BoldOutlined />}
                          onClick={() => applyInlineFormat('**')}
                          disabled={formattingDisabled}
                          aria-label={t('orders:orderDetail.emailFormattingBold')}
                        />
                      </Tooltip>
                      <Tooltip title={t('orders:orderDetail.emailFormattingItalic')}>
                        <Button
                          type="text"
                          size="small"
                          icon={<ItalicOutlined />}
                          onClick={() => applyInlineFormat('*')}
                          disabled={formattingDisabled}
                          aria-label={t('orders:orderDetail.emailFormattingItalic')}
                        />
                      </Tooltip>
                      <Tooltip title={t('orders:orderDetail.emailFormattingInlineCode')}>
                        <Button
                          type="text"
                          size="small"
                          icon={<CodeOutlined />}
                          onClick={() => applyInlineFormat('`')}
                          disabled={formattingDisabled}
                          aria-label={t('orders:orderDetail.emailFormattingInlineCode')}
                        />
                      </Tooltip>
                      <Tooltip title={t('orders:orderDetail.emailFormattingCodeBlock')}>
                        <Button
                          type="text"
                          size="small"
                          onClick={applyCodeBlockFormat}
                          disabled={formattingDisabled}
                          aria-label={t('orders:orderDetail.emailFormattingCodeBlock')}
                        >
                          {'{ }'}
                        </Button>
                      </Tooltip>
                    </Space>
                    <Input.TextArea
                      ref={messageTextAreaRef}
                      value={message}
                      onChange={(event) => onMessageChange(event.target.value)}
                      rows={16}
                      maxLength={20000}
                      disabled={sending}
                    />
                    <div style={{
                      marginTop: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      color: '#8c8c8c',
                      fontSize: 12,
                    }}>
                      <span style={{ minWidth: 0 }}>{t('orders:orderDetail.emailMessageHint')}</span>
                      <span style={{ whiteSpace: 'nowrap' }}>{message.length} / 20000</span>
                    </div>
                  </div>
                </Space>
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>{t('orders:orderDetail.emailPreview')}</div>
                <iframe
                  title="customer-email-preview"
                  sandbox=""
                  srcDoc={previewHtml}
                  style={{ width: '100%', height: 420, border: '1px solid #d9d9d9', borderRadius: 6, background: '#ffffff' }}
                />
              </Col>
            </Row>
          ) : (
            <>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>{t('orders:orderDetail.emailPreview')}</div>
              <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 12 }}>
                {t('orders:orderDetail.emailPreviewHint')}
              </div>
            <iframe
              title="customer-email-preview"
              sandbox=""
              srcDoc={previewHtml}
              style={{ width: '100%', height: 520, border: '1px solid #d9d9d9', borderRadius: 6, background: '#ffffff' }}
            />
            </>
          )}
        </Space>
      ) : (
        <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
          {previewErrorText}
        </div>
      )}
    </Modal>
  );
};

export default CustomerEmailEditorModal;
